import { useMutation } from "@tanstack/react-query";
import { Button, ErrorAlert, Field, Input, Logo, useDocumentTitle } from "@unizpay/ui";
import { MailCheck } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { api } from "../lib/api";

/** Centered card on the gray canvas, shared by sign-in and the sign-in callback. */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <Logo className="mb-8 h-9" />
      <main className="w-full max-w-sm rounded-lg border border-line bg-surface px-6 py-7 shadow-sm">
        {children}
      </main>
      <p className="mt-6 max-w-sm text-center text-xs text-fg-muted">
        Non-custodial crypto payments. Funds go straight to your wallet.
      </p>
    </div>
  );
}

export function LoginPage() {
  useDocumentTitle("Sign in");
  const [email, setEmail] = useState("");
  const login = useMutation({ mutationFn: (e: string) => api.auth.login(e) });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    login.mutate(email.trim());
  }

  if (login.isSuccess) {
    return (
      <AuthShell>
        <span className="flex size-10 items-center justify-center rounded-full bg-accent/10 text-accent">
          <MailCheck aria-hidden="true" className="size-5" />
        </span>
        <h1 className="mt-4 text-lg font-semibold">Check your email</h1>
        <p className="mt-1.5 text-[0.8125rem] text-fg-muted">
          We sent a sign-in link to <span className="font-medium text-fg">{login.variables}</span>. The link
          expires in 15 minutes.
        </p>
        <Button variant="secondary" size="lg" className="mt-6 w-full" onClick={() => login.reset()}>
          Use a different email
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="text-lg font-semibold">Sign in to your account</h1>
      <p className="mt-1 text-[0.8125rem] text-fg-muted">
        New here? Signing in creates your account. No password needed.
      </p>
      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <Field label="Email">
          {(p) => (
            <Input
              {...p}
              type="email"
              name="email"
              autoComplete="email"
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          )}
        </Field>
        <ErrorAlert error={login.error} />
        <Button type="submit" size="lg" className="w-full" loading={login.isPending}>
          Email me a sign-in link
        </Button>
      </form>
    </AuthShell>
  );
}
